/**
 * Features Context
 * Provides feature permissions throughout the client portal
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import clientFeaturesService from '../services/clientFeaturesService';

const FeaturesContext = createContext();

const FEATURES_CACHE_TTL = 5 * 60 * 1000;
let featuresCache = { userKey: null, items: null, fetchedAt: 0 };
let featuresRequest = null;

export const useFeatures = () => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
};

export const FeaturesProvider = ({ children }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load features on mount
  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async (force = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      // Los owners de un tenant respetan la misma matriz de permisos que su
      // equipo. Solo el backend puede decidir excepciones de super admin.
      const userKey = user?.id || user?.email || 'anonymous';
      const cacheIsFresh = featuresCache.userKey === userKey
        && Array.isArray(featuresCache.items)
        && Date.now() - featuresCache.fetchedAt < FEATURES_CACHE_TTL;

      if (!force && cacheIsFresh) {
        setFeatures(featuresCache.items);
        return;
      }

      let request;
      if (featuresRequest?.userKey === userKey) {
        request = featuresRequest.promise;
      } else {
        const promise = clientFeaturesService.getMyFeatures().finally(() => {
          if (featuresRequest?.promise === promise) featuresRequest = null;
        });
        featuresRequest = { userKey, promise };
        request = promise;
      }

      const data = await request;
      featuresCache = { userKey, items: data, fetchedAt: Date.now() };
      setFeatures(data);
    } catch (err) {
      console.error('❌ [FEATURES] Error loading features:', err);
      console.error('❌ [FEATURES] Error details:', err.response?.data);
      setError('Error al cargar permisos');
      // If error, set empty features (will hide all optional features)
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if a feature is enabled
   * @param {string} featureKey - Feature key to check
   * @returns {boolean} True if feature is enabled
   */
  const hasFeature = (featureKey) => {
    return clientFeaturesService.isFeatureEnabled(features, featureKey);
  };

  /**
   * Check if any of the features in the list is enabled
   * @param {string[]} featureKeys - Array of feature keys
   * @returns {boolean} True if at least one feature is enabled
   */
  const hasAnyFeature = (featureKeys) => {
    return featureKeys.some(key => hasFeature(key));
  };

  /**
   * Check if all features in the list are enabled
   * @param {string[]} featureKeys - Array of feature keys
   * @returns {boolean} True if all features are enabled
   */
  const hasAllFeatures = (featureKeys) => {
    return featureKeys.every(key => hasFeature(key));
  };

  const value = {
    features,
    loading,
    error,
    hasFeature,
    hasAnyFeature,
    hasAllFeatures,
    reloadFeatures: () => loadFeatures(true)
  };

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
};

export default FeaturesContext;
